import { useState, useEffect } from "react";
import {
  X,
  User,
  Ruler,
  FolderOpen,
  FileText,
  Search,
  LayoutGrid,
  PenLine,
  Smartphone,
  Globe,
  ChevronRight,
  ArrowRight,
  Mail,
} from "lucide-react";
import Logo from "./assets/tseamlogo.png";

/* ─── CSS ─────────────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Mono:wght@300;400;500&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink:          #1a1916;
    --ink-muted:    #706c64;
    --ink-faint:    #a09c94;
    --bg:           #f7f5f2;
    --surface:      #ffffff;
    --border:       #e2e0dc;
    --border-strong:#c8c4bc;
    --accent-fg:    #fafaf8;
    --serif:        'Cormorant Garamond', Georgia, serif;
    --mono:         'DM Mono', 'Courier New', monospace;
    --sans:         'DM Sans', sans-serif;
    --radius:       4px;
    --ease:         0.2s ease;
  }

  html { scroll-behavior: smooth; }
  body { font-family: var(--sans); background: var(--bg); color: var(--ink); -webkit-font-smoothing: antialiased; }
  a { color: inherit; text-decoration: none; }

  /* ── POPUP ── */
  .ts-overlay {
    position: fixed; inset: 0;
    background: rgba(26,25,22,.6);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 20px;
    opacity: 0; pointer-events: none;
    transition: opacity .3s ease;
  }
  .ts-overlay.visible { opacity: 1; pointer-events: all; }
  .ts-popup {
    background: var(--surface); border-radius: 2px;
    width: 100%; max-width: 460px; padding: 40px 36px;
    text-align: center;
    box-shadow: 0 20px 60px rgba(26,25,22,.2);
    transform: translateY(16px); transition: transform .3s ease;
    position: relative;
  }
  .ts-overlay.visible .ts-popup { transform: translateY(0); }
  .ts-popup-close {
    position: absolute; top: 16px; right: 20px;
    background: none; border: none; cursor: pointer;
    color: var(--ink-faint); line-height: 1;
    transition: color var(--ease); display: flex; align-items: center;
  }
  .ts-popup-close:hover { color: var(--ink); }
  .ts-popup-eyebrow {
    font-family: var(--mono); font-size: 10px;
    letter-spacing: .12em; text-transform: uppercase;
    color: var(--ink-muted); margin-bottom: 14px;
  }
  .ts-popup-title {
    font-family: var(--serif); font-size: 32px; font-weight: 500;
    line-height: 1.2; margin-bottom: 10px;
  }
  .ts-popup-title em { font-style: italic; opacity: .6; }
  .ts-popup-sub { font-size: 14px; color: var(--ink-muted); margin-bottom: 28px; line-height: 1.6; }
  .ts-popup-actions { display: flex; flex-direction: column; gap: 10px; }
  .ts-btn-dl {
    display: flex; align-items: center; justify-content: center; gap: 10px;
    padding: 13px 20px; border-radius: var(--radius);
    font-family: var(--sans); font-size: 14px; font-weight: 500;
    cursor: pointer; border: 1px solid transparent;
    transition: all var(--ease); text-decoration: none;
  }
  .ts-btn-dl-primary { background: var(--ink); color: var(--accent-fg); border-color: var(--ink); }
  .ts-btn-dl-primary:hover { opacity: .85; }
  .ts-btn-dl-ghost { background: transparent; color: var(--ink); border-color: var(--border-strong); }
  .ts-btn-dl-ghost:hover { border-color: var(--ink); background: var(--bg); }
  .ts-popup-web {
    margin-top: 18px; padding-top: 18px;
    border-top: 1px solid var(--border);
  }
  .ts-popup-web span { font-size: 13px; color: var(--ink-muted); }
  .ts-popup-web a {
    font-family: var(--mono); font-size: 12px;
    color: var(--ink); text-decoration: underline; text-underline-offset: 3px;
    margin-left: 6px;
  }

  /* ── NAV ── */
  nav {
    position: sticky; top: 0; z-index: 100;
    background: var(--ink);
    border-bottom: 1px solid rgba(250,250,248,.08);
  }
  .nav-inner {
    max-width: 1080px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; justify-content: space-between;
    height: 60px;
  }
  .nav-brand { display: flex; align-items: center; }
  .nav-logo-mark {
    width: 70px; height: 400px; border-radius: 4px;
    overflow: hidden; display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .nav-logo-img {
    width: 100%; height: 100%;
    object-fit: contain;
  }
  .nav-brand-name {
    font-family: var(--serif); font-size: 20px; font-weight: 500;
    color: var(--accent-fg); letter-spacing: .02em;
  }
  .nav-links { display: flex; align-items: center; gap: 28px; list-style: none; }
  .nav-links a {
    font-family: var(--mono); font-size: 11px;
    letter-spacing: .08em; text-transform: uppercase;
    color: rgba(250,250,248,.55); transition: color var(--ease);
  }
  .nav-links a:hover { color: var(--accent-fg); }
  .nav-cta {
    font-family: var(--mono); font-size: 11px;
    letter-spacing: .06em; text-transform: uppercase;
    padding: 8px 16px; border-radius: var(--radius);
    border: 1px solid rgba(250,250,248,.25);
    background: transparent; color: var(--accent-fg);
    cursor: pointer; transition: all var(--ease);
  }
  .nav-cta:hover { background: rgba(250,250,248,.1); border-color: rgba(250,250,248,.5); }

  /* ── HERO ── */
  .hero {
    min-height: 92vh;
    display: flex; flex-direction: column; justify-content: center;
    padding: 80px 32px 64px;
    background: var(--ink); color: var(--accent-fg);
    position: relative; overflow: hidden;
  }
  .hero-grid {
    position: absolute; inset: 0; opacity: .04;
    background-image:
      linear-gradient(rgba(250,250,248,.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(250,250,248,.4) 1px, transparent 1px);
    background-size: 48px 48px;
  }
  .hero-inner { max-width: 1080px; margin: 0 auto; width: 100%; position: relative; z-index: 1; }
  .hero-eyebrow {
    font-family: var(--mono); font-size: 11px;
    letter-spacing: .12em; text-transform: uppercase;
    color: rgba(250,250,248,.45); margin-bottom: 24px;
    display: flex; align-items: center; gap: 10px;
  }
  .hero-eyebrow::before {
    content: ''; display: block; width: 32px; height: 1px;
    background: rgba(250,250,248,.3);
  }
  .hero-headline {
    font-family: var(--serif);
    font-size: clamp(52px, 8vw, 96px);
    font-weight: 500; line-height: 1.0; letter-spacing: -.01em;
    margin-bottom: 28px;
  }
  .hero-headline em { font-style: italic; opacity: .6; }
  .hero-body {
    font-size: 17px; line-height: 1.7; max-width: 480px;
    color: rgba(250,250,248,.65); margin-bottom: 48px;
  }
  .hero-actions { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; }
  .btn-hero-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--bg); color: var(--ink);
    padding: 14px 28px; border-radius: var(--radius);
    font-family: var(--sans); font-size: 15px; font-weight: 500;
    cursor: pointer; border: none; transition: all var(--ease); white-space: nowrap;
  }
  .btn-hero-primary:hover { background: #fff; transform: translateY(-1px); }
  .btn-hero-ghost {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: var(--accent-fg);
    padding: 14px 28px; border-radius: var(--radius);
    font-family: var(--sans); font-size: 15px; font-weight: 400;
    cursor: pointer; border: 1px solid rgba(250,250,248,.2);
    transition: all var(--ease); white-space: nowrap; text-decoration: none;
  }
  .btn-hero-ghost:hover { border-color: rgba(250,250,248,.5); }
  .hero-stats {
    margin-top: 72px; padding-top: 40px;
    border-top: 1px solid rgba(250,250,248,.1);
    display: flex; gap: 56px; flex-wrap: wrap;
  }
  .stat-num { font-family: var(--serif); font-size: 36px; font-weight: 500; line-height: 1; }
  .stat-label {
    font-family: var(--mono); font-size: 10px;
    letter-spacing: .1em; text-transform: uppercase;
    color: rgba(250,250,248,.4); margin-top: 6px;
  }

  /* ── SECTIONS ── */
  section { padding: 96px 32px; }
  .section-inner { max-width: 1080px; margin: 0 auto; }
  .section-label {
    font-family: var(--mono); font-size: 10px;
    letter-spacing: .12em; text-transform: uppercase;
    color: var(--ink-faint); margin-bottom: 16px;
  }
  .section-title {
    font-family: var(--serif);
    font-size: clamp(36px, 5vw, 56px);
    font-weight: 500; line-height: 1.1; margin-bottom: 20px;
  }
  .section-sub { font-size: 16px; color: var(--ink-muted); max-width: 520px; line-height: 1.7; }

  /* ── HOW IT WORKS ── */
  #how { background: var(--surface); }
  .steps-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1px; border: 1px solid var(--border); border-radius: var(--radius);
    overflow: hidden; margin-top: 56px;
  }
  .step-card { padding: 36px 28px; background: var(--surface); transition: background var(--ease); }
  .step-card:hover { background: var(--bg); }
  .step-num { font-family: var(--mono); font-size: 11px; letter-spacing: .1em; color: var(--ink-faint); margin-bottom: 16px; }
  .step-icon { margin-bottom: 14px; color: var(--ink); }
  .step-title { font-family: var(--serif); font-size: 22px; font-weight: 500; margin-bottom: 10px; }
  .step-desc { font-size: 14px; color: var(--ink-muted); line-height: 1.7; }

  /* ── FEATURES ── */
  #features { background: var(--bg); }
  .features-header { margin-bottom: 60px; }
  .features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; }
  .feature-card {
    border: 1px solid var(--border); border-radius: var(--radius);
    padding: 32px 28px; background: var(--surface);
    transition: border-color var(--ease), box-shadow var(--ease);
  }
  .feature-card:hover { border-color: var(--border-strong); box-shadow: 0 4px 20px rgba(26,25,22,.07); }
  .feature-icon {
    width: 40px; height: 40px; background: var(--ink); border-radius: var(--radius);
    display: flex; align-items: center; justify-content: center; margin-bottom: 20px;
  }
  .feature-name { font-family: var(--serif); font-size: 20px; font-weight: 500; margin-bottom: 8px; }
  .feature-desc { font-size: 14px; color: var(--ink-muted); line-height: 1.7; }

  /* ── OUTFIT TYPES ── */
  .outfits-section { background: var(--ink); color: var(--accent-fg); }
  .outfits-section .section-sub { color: rgba(250,250,248,.55); }
  .outfits-section .section-label { color: rgba(250,250,248,.35); }
  .outfit-tags { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 48px; }
  .outfit-tag {
    font-family: var(--mono); font-size: 12px;
    letter-spacing: .06em; text-transform: uppercase;
    padding: 8px 18px; border: 1px solid rgba(250,250,248,.15);
    border-radius: 100px; color: rgba(250,250,248,.65);
    transition: all var(--ease);
  }
  .outfit-tag:hover { border-color: rgba(250,250,248,.5); color: var(--accent-fg); }
  .outfit-tag.highlight { border-color: rgba(250,250,248,.4); color: var(--accent-fg); }

  /* ── PDF CTA ── */
  #pdf-cta { background: var(--bg); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
  .pdf-cta-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
  .pdf-preview {
    background: var(--ink); border-radius: var(--radius); padding: 32px;
    font-family: var(--mono); font-size: 11px; color: rgba(250,250,248,.7);
    line-height: 1.8; box-shadow: 0 12px 40px rgba(26,25,22,.15);
  }
  .pdf-header-line {
    font-family: var(--serif); font-size: 16px; font-weight: 500;
    color: var(--accent-fg); margin-bottom: 16px; padding-bottom: 12px;
    border-bottom: 1px solid rgba(250,250,248,.15);
  }
  .pdf-row { display: flex; justify-content: space-between; border-bottom: 1px solid rgba(250,250,248,.08); padding: 5px 0; }
  .pdf-row:last-child { border-bottom: none; }
  .pdf-row-key { opacity: .5; }
  .pdf-row-val { font-weight: 500; color: rgba(250,250,248,.9); }
  .btn-pdf-cta {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--ink); color: var(--accent-fg);
    padding: 14px 28px; border-radius: var(--radius);
    font-family: var(--sans); font-size: 15px; font-weight: 500;
    cursor: pointer; border: none; transition: all var(--ease);
  }
  .btn-pdf-cta:hover { opacity: .85; transform: translateY(-1px); }

  /* ── DOWNLOAD ── */
  #download { background: var(--ink); color: var(--accent-fg); text-align: center; }
  #download .section-title { color: var(--accent-fg); }
  #download .section-sub { color: rgba(250,250,248,.55); margin: 0 auto 48px; }
  .download-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px; }
  .btn-store {
    display: inline-flex; align-items: center; gap: 12px;
    padding: 14px 24px; border-radius: var(--radius);
    border: 1px solid rgba(250,250,248,.2);
    background: transparent; color: var(--accent-fg);
    font-family: var(--sans); cursor: pointer;
    transition: all var(--ease); min-width: 180px; text-decoration: none;
  }
  .btn-store:hover { background: rgba(250,250,248,.08); border-color: rgba(250,250,248,.45); }
  .btn-store-text { text-align: left; }
  .btn-store-sub { font-size: 10px; opacity: .5; letter-spacing: .04em; display: block; }
  .btn-store-name { font-size: 15px; font-weight: 500; display: block; }
  .web-version-link { margin-top: 20px; font-family: var(--mono); font-size: 12px; color: rgba(250,250,248,.4); letter-spacing: .04em; }
  .web-version-link a { color: rgba(250,250,248,.7); text-decoration: underline; text-underline-offset: 3px; transition: color var(--ease); }
  .web-version-link a:hover { color: var(--accent-fg); }

  /* ── LEGAL PAGES ── */
  .legal-page { padding: 64px 32px; min-height: 80vh; }
  .legal-inner { max-width: 720px; margin: 0 auto; }
  .legal-back {
    background: none; border: none; cursor: pointer;
    font-family: var(--mono); font-size: 11px;
    letter-spacing: .08em; text-transform: uppercase;
    color: var(--ink-muted); margin-bottom: 40px; padding: 0;
    display: flex; align-items: center; gap: 8px; transition: color var(--ease);
  }
  .legal-back:hover { color: var(--ink); }
  .legal-heading { font-family: var(--serif); font-size: 48px; font-weight: 500; margin-bottom: 8px; }
  .legal-date { font-family: var(--mono); font-size: 11px; color: var(--ink-faint); margin-bottom: 48px; }
  .legal-body h2 { font-family: var(--serif); font-size: 24px; font-weight: 500; margin: 40px 0 12px; }
  .legal-body p, .legal-body li { font-size: 15px; color: var(--ink-muted); line-height: 1.8; margin-bottom: 14px; }
  .legal-body ul { padding-left: 20px; }
  .legal-body li { margin-bottom: 6px; }
  .legal-body a { color: var(--ink); text-decoration: underline; text-underline-offset: 3px; }

  /* ── FOOTER ── */
  footer {
    background: var(--ink); color: var(--accent-fg);
    padding: 48px 32px; border-top: 1px solid rgba(250,250,248,.08);
  }
  .footer-inner {
    max-width: 1080px; margin: 0 auto;
    display: flex; justify-content: space-between;
    align-items: flex-end; flex-wrap: wrap; gap: 32px;
  }
  .footer-brand-name { font-family: var(--serif); font-size: 22px; font-weight: 500; margin-bottom: 6px; }
  .footer-tagline { font-family: var(--mono); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; color: rgba(250,250,248,.35); }
  .footer-links { display: flex; gap: 24px; flex-wrap: wrap; }
  .footer-links a, .footer-links button {
    font-family: var(--mono); font-size: 11px; letter-spacing: .06em; text-transform: uppercase;
    color: rgba(250,250,248,.4); transition: color var(--ease);
    background: none; border: none; cursor: pointer; padding: 0;
  }
  .footer-links a:hover, .footer-links button:hover { color: var(--accent-fg); }
  .footer-copy {
    font-family: var(--mono); font-size: 10px; color: rgba(250,250,248,.25);
    margin: 32px auto 0; text-align: center; max-width: 1080px;
    padding-top: 24px; border-top: 1px solid rgba(250,250,248,.07);
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 768px) {
    .nav-links { display: none; }
    .hero { padding: 64px 20px 48px; }
    section { padding: 72px 20px; }
    .pdf-cta-inner { grid-template-columns: 1fr; gap: 40px; }
    .hero-stats { gap: 36px; }
    .ts-popup { padding: 32px 24px; }
    footer { padding: 40px 20px; }
    .footer-inner { flex-direction: column; align-items: flex-start; }
  }
`;

/* ─── ICONS ───────────────────────────────────────────────────────────── */

const AppleLogo = () => (
  <svg width="22" height="22" viewBox="0 0 814 1000" fill="currentColor">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.3-155.5-98.8c-45.4-55.9-90.5-144.3-90.5-228.7 0-170.5 112.4-260.5 222.4-260.5 57.5 0 105.4 38.4 141.5 38.4 34.7 0 89.3-40.8 155.5-40.8 24.8 0 108.2 2.6 168.6 77.6zm-198.5-166.5c31.5-39.5 54.5-93.8 54.5-148.1 0-7.7-.6-15.4-1.9-21.7-51.9 1.9-112.9 34.7-149.7 79.7-28.5 32.6-55.7 84.5-55.7 141.5 0 8.3 1.3 16.6 1.9 19.2 3.2.6 8.3 1.3 13.4 1.3 46.8 0 103.7-31.5 137.5-71.9z"/>
  </svg>
);

const GooglePlayLogo = () => (
  <svg width="22" height="22" viewBox="0 0 512 512" fill="none">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1z" fill="#00D2FF"/>
    <path d="M47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l2.4 1.4L207.7 256v-4.2L47 1.3 47 0z" fill="#00EFD1"/>
    <path d="M262.7 312.3l-65.4-65.4V248l65.4-65.3 1.4.8 77.5 44c22.1 12.6 22.1 33.1 0 45.7l-77.5 44-1.4.9z" fill="#FFBC00"/>
    <path d="M264.1 311.5L197.3 256 47 406.3c7.3 7.7 19.3 8.6 32.8.8l184.3-95.6z" fill="#FF3A44"/>
    <path d="M264.1 200.5L79.8 104.9C66.3 97.1 54.3 98 47 105.7L197.3 256l66.8-55.5z" fill="#00D2FF"/>
  </svg>
);

/* ─── COMPONENTS ──────────────────────────────────────────────────────── */

const Popup = ({ open, onClose }) => (
  <div className={`ts-overlay${open ? " visible" : ""}`} onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="ts-popup" role="dialog" aria-modal="true">
      <button className="ts-popup-close" onClick={onClose} aria-label="Close"><X size={18}/></button>
      <p className="ts-popup-eyebrow">Get the app</p>
      <h2 className="ts-popup-title">Trackseam<br /><em>on your phone</em></h2>
      <p className="ts-popup-sub">Download the app for iOS or Android and manage your shop from anywhere.</p>
      <div className="ts-popup-actions">
        <a href="#" className="ts-btn-dl ts-btn-dl-primary" onClick={onClose}>
          <AppleLogo /> Download on the App Store
        </a>
        <a href="#" className="ts-btn-dl ts-btn-dl-ghost" onClick={onClose}>
          <GooglePlayLogo /> Get it on Google Play
        </a>
      </div>
      <div className="ts-popup-web">
        <span>Prefer the browser?</span>
        <a href="https://app.trackseam.com" target="_blank" rel="noopener">Open web version →</a>
      </div>
    </div>
  </div>
);

const Nav = ({ onDownload }) => (
  <nav>
    <div className="nav-inner">
      <div className="nav-brand">
        <div className="nav-logo-mark">
          <img src={Logo} alt="Trackseam logo" className="nav-logo-img" />
        </div>
        <span className="nav-brand-name">Trackseam</span>
      </div>
      <ul className="nav-links">
        <li><a href="#how">How it works</a></li>
        <li><a href="#features">Features</a></li>
        <li><a href="#download">Download</a></li>
      </ul>
      <button className="nav-cta" onClick={onDownload}>Get the app</button>
    </div>
  </nav>
);

const Hero = ({ onDownload }) => (
  <section className="hero">
    <div className="hero-grid" />
    <div className="hero-inner">
      <p className="hero-eyebrow">Built for tailors &amp; fashion designers</p>
      <h1 className="hero-headline">
        Every client.<br />
        Every stitch.<br />
        <em>Perfectly measured.</em>
      </h1>
      <p className="hero-body">
        Trackseam is the measurement management tool built for tailors, fashion designers, and bespoke studios. Store client profiles, track measurements across outfit types, and export professional PDFs — all in one place.
      </p>
      <div className="hero-actions">
        <button className="btn-hero-primary" onClick={onDownload}>
          Download the app <ChevronRight size={16}/>
        </button>
        <a className="btn-hero-ghost" href="https://app.trackseam.com" target="_blank" rel="noopener">
          Try the web version <ArrowRight size={16}/>
        </a>
      </div>
      <div className="hero-stats">
        {[
          { num: "8+", label: "Outfit types" },
          { num: "PDF", label: "Export ready" },
          { num: "∞", label: "Clients & records" },
          { num: "iOS + Android", label: "Cross-platform" },
        ].map((s) => (
          <div key={s.label}>
            <div className="stat-num">{s.num}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const steps = [
  { num: "01", icon: <User size={28}/>, title: "Add a client", desc: "Create a client profile with their name, phone, and email. Each client gets a unique ID for easy reference." },
  { num: "02", icon: <Ruler size={28}/>, title: "Record measurements", desc: "Select the outfit type and fill in the relevant fields. Smart field sets for shirts, trousers, dresses, suits, Agbada, Kaftan, and more." },
  { num: "03", icon: <FolderOpen size={28}/>, title: "Build a history", desc: "Every measurement is stored against the client. Track how measurements change over time across multiple garments." },
  { num: "04", icon: <FileText size={28}/>, title: "Export a PDF", desc: "Generate a clean, professional measurement record PDF branded with your shop name — ready to share or file away." },
];

const HowItWorks = () => (
  <section id="how">
    <div className="section-inner">
      <p className="section-label">How it works</p>
      <h2 className="section-title">From first fitting<br />to final stitch</h2>
      <p className="section-sub">A simple, focused workflow that keeps you organised and your clients happy.</p>
      <div className="steps-grid">
        {steps.map((s) => (
          <div className="step-card" key={s.num}>
            <p className="step-num">{s.num}</p>
            <div className="step-icon">{s.icon}</div>
            <h3 className="step-title">{s.title}</h3>
            <p className="step-desc">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const features = [
  { icon: <Search size={18} color="#fafaf8"/>, name: "Quick client search", desc: "Find any client instantly by name or ID. No more flipping through notebooks or scrolling endlessly." },
  { icon: <LayoutGrid size={18} color="#fafaf8"/>, name: "Smart outfit fields", desc: "Pre-filled measurement fields for every outfit type. The right fields appear automatically when you select Shirt, Trouser, Dress, Suit, and more." },
  { icon: <PenLine size={18} color="#fafaf8"/>, name: "Custom measurements", desc: "Need something bespoke? Add custom fields for specialty garments that don't fit standard templates." },
  { icon: <FileText size={18} color="#fafaf8"/>, name: "Professional PDF export", desc: "Export branded measurement records with your shop name, client details, and all measurements in a clean A4 layout." },
  { icon: <Smartphone size={18} color="#fafaf8"/>, name: "Native mobile app", desc: "iOS and Android apps that feel right on your phone — with native share sheets for sending PDFs directly from the app." },
  { icon: <Globe size={18} color="#fafaf8"/>, name: "Web version available", desc: "Prefer your laptop or desktop? Access your full account and all client records at app.trackseam.com from any browser." },
];

const Features = () => (
  <section id="features">
    <div className="section-inner">
      <div className="features-header">
        <p className="section-label">Features</p>
        <h2 className="section-title">Everything your shop needs</h2>
        <p className="section-sub">Designed for daily use in a real tailor shop — not a generic CRM with measurement fields bolted on.</p>
      </div>
      <div className="features-grid">
        {features.map((f) => (
          <div className="feature-card" key={f.name}>
            <div className="feature-icon">{f.icon}</div>
            <h3 className="feature-name">{f.name}</h3>
            <p className="feature-desc">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const outfitTags = [
  { label: "Agbada", highlight: true },
  { label: "Kaftan", highlight: true },
  { label: "Shirt" }, { label: "Trouser" }, { label: "Dress" },
  { label: "Suit" }, { label: "Skirt" }, { label: "Custom" },
];

const OutfitTypes = () => (
  <section className="outfits-section">
    <div className="section-inner">
      <p className="section-label">Outfit types</p>
      <h2 className="section-title">Built for African<br />and Western fashion</h2>
      <p className="section-sub">Trackseam ships with measurement templates for the garments you actually make — including traditional African styles alongside classic Western cuts.</p>
      <div className="outfit-tags">
        {outfitTags.map((t) => (
          <span key={t.label} className={`outfit-tag${t.highlight ? " highlight" : ""}`}>{t.label}</span>
        ))}
      </div>
    </div>
  </section>
);

const pdfRows = [
  { key: "Client Name", val: "Adebayo Okonkwo" },
  { key: "Client ID", val: "TS-4E8F2C" },
  { key: "Outfit Type", val: "Agbada" },
  { key: "Chest", val: "104 cm" },
  { key: "Waist", val: "92 cm" },
  { key: "Shoulder", val: "48 cm" },
  { key: "Sleeve Length", val: "65 cm" },
  { key: "Gown Length", val: "140 cm" },
  { key: "Inseam", val: "78 cm" },
];

const PdfCta = ({ onDownload }) => (
  <section id="pdf-cta">
    <div className="section-inner">
      <div className="pdf-cta-inner">
        <div>
          <p className="section-label">PDF export</p>
          <h2 className="section-title">Measurement records that look the part</h2>
          <p className="section-sub" style={{ marginBottom: 32 }}>Every PDF is branded with your shop name, timestamped, and laid out cleanly so you can file it or hand it off to a client with confidence.</p>
          <button className="btn-pdf-cta" onClick={onDownload}>
            Download Trackseam <ChevronRight size={16}/>
          </button>
        </div>
        <div className="pdf-preview">
          <div className="pdf-header-line">Measurement Record</div>
          {pdfRows.map((r) => (
            <div className="pdf-row" key={r.key}>
              <span className="pdf-row-key">{r.key}</span>
              <span className="pdf-row-val">{r.val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Download = () => (
  <section id="download">
    <div className="section-inner">
      <p className="section-label" style={{ color: "rgba(250,250,248,.35)" }}>Get started today</p>
      <h2 className="section-title">Your shop, organised</h2>
      <p className="section-sub">Download Trackseam for free and bring order to every fitting.</p>
      <div className="download-actions">
        <a href="#" className="btn-store">
          <AppleLogo />
          <span className="btn-store-text">
            <span className="btn-store-sub">Download on the</span>
            <span className="btn-store-name">App Store</span>
          </span>
        </a>
        <a href="#" className="btn-store">
          <GooglePlayLogo />
          <span className="btn-store-text">
            <span className="btn-store-sub">Get it on</span>
            <span className="btn-store-name">Google Play</span>
          </span>
        </a>
      </div>
      <p className="web-version-link">
        Prefer the browser?{" "}
        <a href="https://app.trackseam.com" target="_blank" rel="noopener">Open the web version at app.trackseam.com →</a>
      </p>
    </div>
  </section>
);

/* ─── LEGAL PAGES ──────────────────────────────────────────────────────── */

const TermsPage = ({ onBack }) => (
  <div className="legal-page">
    <div className="legal-inner">
      <button className="legal-back" onClick={onBack}>← Back to Trackseam</button>
      <h1 className="legal-heading">Terms of Use</h1>
      <p className="legal-date">Last updated: June 2025</p>
      <div className="legal-body">
        <p>By accessing or using Trackseam (the "Service"), you agree to be bound by these Terms of Use. Please read them carefully.</p>
        <h2>1. Use of the Service</h2>
        <p>Trackseam provides tools for tailors and fashion professionals to manage client measurement records. You may use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:</p>
        <ul>
          <li>In any way that violates applicable laws or regulations.</li>
          <li>To transmit unsolicited or unauthorised advertising or promotional material.</li>
          <li>To attempt to gain unauthorised access to any part of the Service or its systems.</li>
        </ul>
        <h2>2. Accounts</h2>
        <p>To use Trackseam you must create an account with a valid email address and a password. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at support@trackseam.com if you suspect unauthorised access.</p>
        <h2>3. Your Data</h2>
        <p>You retain ownership of all client data and measurement records you upload or create in Trackseam. By using the Service, you grant us a limited licence to store and process that data solely for the purpose of providing the Service to you.</p>
        <h2>4. Acceptable Use</h2>
        <p>You agree to use Trackseam only for managing legitimate tailor or fashion business data. You must not upload false, misleading, or unlawfully obtained information about individuals.</p>
        <h2>5. Availability</h2>
        <p>We aim to keep Trackseam available at all times but do not guarantee uninterrupted access. We may suspend, withdraw, or restrict the Service for business or operational reasons with reasonable notice where possible.</p>
        <h2>6. Intellectual Property</h2>
        <p>The Trackseam name, logo, application design, and software are owned by Trackseam and are protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works from any part of the Service without our express written permission.</p>
        <h2>7. Limitation of Liability</h2>
        <p>To the maximum extent permitted by law, Trackseam shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with your use of the Service, including loss of data. Our total aggregate liability shall not exceed the amount paid by you (if any) in the twelve months preceding the claim.</p>
        <h2>8. Termination</h2>
        <p>We may suspend or terminate your access to the Service at any time for violation of these Terms. You may delete your account at any time by contacting us at support@trackseam.com.</p>
        <h2>9. Changes to These Terms</h2>
        <p>We may update these Terms from time to time. We will notify you of material changes by posting a notice in the app or by email. Continued use of the Service after changes take effect constitutes your acceptance of the updated Terms.</p>
        <h2>10. Contact</h2>
        <p>For questions about these Terms, please contact us at <a href="mailto:support@trackseam.com">support@trackseam.com</a>.</p>
      </div>
    </div>
  </div>
);

const PrivacyPage = ({ onBack }) => (
  <div className="legal-page">
    <div className="legal-inner">
      <button className="legal-back" onClick={onBack}>← Back to Trackseam</button>
      <h1 className="legal-heading">Privacy Policy</h1>
      <p className="legal-date">Last updated: June 2025</p>
      <div className="legal-body">
        <p>Trackseam ("we", "our", "us") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and your rights.</p>
        <h2>1. Data We Collect</h2>
        <p>When you use Trackseam, we collect:</p>
        <ul>
          <li><strong>Account data:</strong> your email address, shop name, and hashed password.</li>
          <li><strong>Client data:</strong> measurement records, client names, phone numbers, and email addresses you enter.</li>
          <li><strong>Usage data:</strong> information about how you interact with the app, including device type, OS version, and anonymised feature usage.</li>
          <li><strong>Technical data:</strong> IP address, browser type, and app version for security and performance monitoring.</li>
        </ul>
        <h2>2. How We Use Your Data</h2>
        <p>We use your data to:</p>
        <ul>
          <li>Provide, operate, and improve the Trackseam Service.</li>
          <li>Authenticate your account and keep it secure.</li>
          <li>Respond to support requests.</li>
          <li>Send important service-related communications (not marketing, unless you opt in).</li>
        </ul>
        <p>We do not sell your personal data to third parties.</p>
        <h2>3. Client Data You Enter</h2>
        <p>Any client information (names, measurements, contact details) you store in Trackseam belongs to you. We act as a data processor on your behalf. You are responsible for ensuring you have appropriate consent from your clients to store their information.</p>
        <h2>4. Data Storage and Security</h2>
        <p>Your data is stored on secure servers. We use industry-standard encryption in transit (TLS) and at rest. Access to production data is restricted to authorised personnel only.</p>
        <h2>5. Data Retention</h2>
        <p>We retain your account and client data for as long as your account is active. If you delete your account, we will remove your data within 30 days except where retention is required by law.</p>
        <h2>6. Your Rights</h2>
        <p>Depending on your location, you may have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you.</li>
          <li>Correct inaccurate data.</li>
          <li>Request deletion of your data.</li>
          <li>Object to or restrict certain processing.</li>
          <li>Data portability.</li>
        </ul>
        <p>To exercise any of these rights, contact us at <a href="mailto:support@trackseam.com">support@trackseam.com</a>.</p>
        <h2>7. Cookies</h2>
        <p>The web version of Trackseam uses essential cookies to maintain your session. We do not use advertising or tracking cookies.</p>
        <h2>8. Third-Party Services</h2>
        <p>Trackseam uses the following third-party service providers to operate:</p>
        <ul><li><strong>Render</strong> — cloud hosting and API infrastructure.</li></ul>
        <p>These providers process data on our behalf and are bound by appropriate data protection agreements.</p>
        <h2>9. Children's Privacy</h2>
        <p>Trackseam is not intended for use by children under 16. We do not knowingly collect personal data from anyone under 16.</p>
        <h2>10. Changes to This Policy</h2>
        <p>We may update this Privacy Policy periodically. We will notify you of significant changes via the app or by email.</p>
        <h2>11. Contact Us</h2>
        <p>For privacy-related questions or requests, contact us at <a href="mailto:support@trackseam.com">support@trackseam.com</a>.</p>
      </div>
    </div>
  </div>
);

/* ─── FOOTER ───────────────────────────────────────────────────────────── */

const Footer = ({ onShowTerms, onShowPrivacy }) => (
  <footer>
    <div className="footer-inner">
      <div>
        <div className="footer-brand-name">Trackseam</div>
        <div className="footer-tagline">Measurement management for tailors</div>
      </div>
      <div className="footer-links">
        <a href="https://app.trackseam.com" target="_blank" rel="noopener">Web App</a>
        <button onClick={onShowTerms}>Terms of Use</button>
        <button onClick={onShowPrivacy}>Privacy Policy</button>
        <a href="mailto:support@trackseam.com">Contact</a>
      </div>
    </div>
    <div className="footer-copy">© 2025 Trackseam. All rights reserved.</div>
  </footer>
);

/* ─── APP ──────────────────────────────────────────────────────────────── */

export default function Trackseam() {
  const [popup, setPopup] = useState(false);
  const [page, setPage] = useState("main"); // "main" | "terms" | "privacy"

  useEffect(() => {
    const shown = sessionStorage.getItem("ts_popup_shown");
    if (!shown) {
      const t = setTimeout(() => {
        setPopup(true);
        sessionStorage.setItem("ts_popup_shown", "1");
      }, 4000);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setPopup(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  const showPage = (p) => setPage(p);
  const showMain = () => setPage("main");

  return (
    <>
      <style>{css}</style>

      <Popup open={popup} onClose={() => setPopup(false)} />

      <Nav onDownload={() => setPopup(true)} />

      {page === "main" && (
        <div>
          <Hero onDownload={() => setPopup(true)} />
          <HowItWorks />
          <Features />
          <OutfitTypes />
          <PdfCta onDownload={() => setPopup(true)} />
          <Download />
        </div>
      )}

      {page === "terms" && <TermsPage onBack={showMain} />}
      {page === "privacy" && <PrivacyPage onBack={showMain} />}

      <Footer onShowTerms={() => showPage("terms")} onShowPrivacy={() => showPage("privacy")} />
    </>
  );
}